import React, {type PropsWithChildren, useEffect, useMemo} from 'react';
import type {ThesaurusId} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {selectSettingsPreferredLocale} from 'services/settings';
import type {SetLoadStatus} from 'services/utils/hooks';
import {useBatchedLoadStatus} from 'services/utils/hooks';
import {useAppSelector, useAppStore} from 'store/hooks';
import {SceneError} from 'components/scene/SceneError';
import {SceneLoader} from 'components/scene/SceneLoader';
import {useGetThesaurusQuery} from '../thesaurusApi';
import {selectThesaurusData} from '../thesaurusSelectors';

type Props = {
  thesauruses: ThesaurusId[] | undefined;
};

/**
 * Triggers and subscribes to a single thesaurus via RTK Query. The subscription
 * keeps the cache entry alive while the gate is mounted so consumer hooks can
 * read it.
 */
const ThesaurusLoader: React.FC<{
  id: ThesaurusId;
  onStatus: SetLoadStatus;
}> = ({id, onStatus}) => {
  const language = useAppSelector(selectSettingsPreferredLocale);
  const {data, isError} = useGetThesaurusQuery(
    {
      id,
      language,
    },
    {
      refetchOnReconnect: true,
    },
  );

  useEffect(() => {
    // Reuse old data if it exist. A loaded thesaurus is never reported as failed.
    if (data) {
      onStatus(id, 'Loaded');
    } else if (isError) {
      onStatus(id, 'Failed');
    } else {
      onStatus(id, 'Loading');
    }
  }, [data, id, isError, onStatus]);

  return null;
};

/**
 * Will only render children after thesauruses are successfully loaded.
 * Shows spinner while loading, and an error message if any fails
 */
export const ThesaurusesGate: React.FC<PropsWithChildren<Props>> = ({
  thesauruses,
  children,
}) => {
  const {t} = useLocalization();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const store = useAppStore();

  // Only mount a sub-loader for ids that aren't already cached (e.g. by an
  // outer gate). Computed once per thesauruses/language change — we don't
  // want this list to shrink mid-lifecycle, since each sub-loader holds the
  // RTK Query subscription that keeps its cache entry alive.
  const notLoadedIds = useMemo<ThesaurusId[]>(() => {
    const ids = thesauruses ?? [];
    const state = store.getState();
    return ids.filter(id => !selectThesaurusData(id, language)(state));
  }, [thesauruses, language, store]);

  const {status, setStatus} = useBatchedLoadStatus(notLoadedIds);

  return (
    <>
      {notLoadedIds.map(id => (
        <ThesaurusLoader key={id} id={id} onStatus={setStatus} />
      ))}
      {status === 'Failed' ? (
        <SceneError error={t('thesaurus.load.failed.label')} applySceneHeight />
      ) : status === 'Loading' ? (
        <SceneLoader
          loadingType={t('general.entity.thesaurus')}
          applySceneHeight
        />
      ) : (
        children
      )}
    </>
  );
};
